import React, { useState, useEffect } from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { Select, Input } from '@/shared/components/ui';
import { UploadInput } from '@/shared/components/ui/UploadInput';
import type { TemplateHeaderMedia, WhatsAppTemplateComponent } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
interface WhatsAppBusinessFormProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
}

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', description: 'Mensagens promocionais e ofertas' },
  { value: 'UTILITY', label: 'Utilidade', description: 'Atualizações transacionais' },
  { value: 'AUTHENTICATION', label: 'Autenticação', description: 'OTP e verificação' },
];

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en_US', label: 'Inglês (EUA)' },
  { value: 'es', label: 'Espanhol' },
  { value: 'en', label: 'Inglês' },
];

type FormatoDeCabecalho = NonNullable<WhatsAppTemplateComponent['format']>;

const HEADER_FORMATS: Array<{ value: FormatoDeCabecalho; label: string; icon: IconName }> = [
  { value: 'TEXT', label: 'Texto', icon: 'message-square' },
  { value: 'IMAGE', label: 'Imagem', icon: 'image' },
  { value: 'VIDEO', label: 'Vídeo', icon: 'video' },
  { value: 'DOCUMENT', label: 'Documento', icon: 'file' },
  { value: 'LOCATION', label: 'Localização', icon: 'map-pin' },
];

/**
 * Formatos de cabeçalho que carregam arquivo — só estes abrem o campo de anexo.
 *
 * Cabeçalho de TEXTO com anexo configurado é descartado em silêncio no envio: o
 * provider monta o header do texto e recusa um segundo header (dois headers no
 * mesmo disparo é (#100) na Meta), então o anexo some sem erro nenhum. Por isso
 * a tela nem oferece o campo, e trocar o formato limpa o que ficou para trás.
 */
const FORMATOS_COM_ARQUIVO = ['IMAGE', 'VIDEO', 'DOCUMENT'] as const;
type FormatoComArquivo = (typeof FORMATOS_COM_ARQUIVO)[number];

const TIPO_DA_MIDIA: Record<FormatoComArquivo, TemplateHeaderMedia['type']> = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
};

/** Filtro do seletor de arquivo por formato (o backend valida o mimetype real). */
const ARQUIVOS_ACEITOS: Record<FormatoComArquivo, string> = {
  IMAGE: 'image/*',
  VIDEO: 'video/*',
  DOCUMENT: '*/*',
};

/**
 * O cabeçalho já carrega o identificador do arquivo devolvido pela Meta?
 * Template aprovado guarda esse identificador em `example.header_handle` — com
 * ele o backend não exige URL, então a tela também não pode cobrar.
 */
const jaTemIdentificadorDaMeta = (componente: WhatsAppTemplateComponent): boolean =>
  (componente.example?.header_handle?.length ?? 0) > 0;

/**
 * Devolve o `example` sem o identificador do arquivo, ou `undefined` quando não
 * sobra mais nada — para não deixar um objeto vazio pendurado no componente.
 * O resto (`header_text`, `body_text`) é preservado.
 */
const semIdentificadorDoArquivo = (
  exemplo: NonNullable<WhatsAppTemplateComponent['example']>,
): WhatsAppTemplateComponent['example'] => {
  const { header_handle: _identificadorAntigo, ...resto } = exemplo;
  return Object.keys(resto).length > 0 ? resto : undefined;
};

const ehFormatoComArquivo = (
  formato: FormatoDeCabecalho | undefined,
): formato is FormatoComArquivo =>
  formato !== undefined && (FORMATOS_COM_ARQUIVO as readonly string[]).includes(formato);

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Resposta Rápida' },
  { value: 'URL', label: 'URL' },
  { value: 'PHONE_NUMBER', label: 'Telefone' },
];

export const WhatsAppBusinessForm: React.FC<WhatsAppBusinessFormProps> = ({
  register,
  watch,
  setValue,
  errors,
}) => {
  const [components, setComponents] = useState<WhatsAppTemplateComponent[]>([
    { type: 'BODY', text: '' }, // Body é obrigatório
  ]);

  // ✅ CRITICAL: Inicializar valores padrão obrigatórios
  useEffect(() => {
    setValue('providerConfig.providerType', 'whatsapp_business');

    // Inicializar category e language se não estiverem definidos
    if (!watch('providerConfig.category')) {
      setValue('providerConfig.category', 'MARKETING');
    }
    if (!watch('providerConfig.language')) {
      setValue('providerConfig.language', 'pt_BR');
    }

    // Inicializar components se não estiverem definidos
    if (!watch('providerConfig.components')) {
      setValue('providerConfig.components', [{ type: 'BODY', text: '' }]);
    }
  }, [setValue]);

  // ✅ CRITICAL: Sincronizar estado local quando providerConfig.components mudar (modo de edição)
  const providerConfigComponents = watch('providerConfig.components');
  useEffect(() => {
    if (providerConfigComponents && providerConfigComponents.length > 0) {
      setComponents(providerConfigComponents);
    }
  }, [JSON.stringify(providerConfigComponents)]);

  const category = watch('providerConfig.category');

  const addComponent = (type: 'HEADER' | 'FOOTER' | 'BUTTONS') => {
    const newComponent: WhatsAppTemplateComponent = { type };

    if (type === 'HEADER') {
      newComponent.format = 'TEXT';
      newComponent.text = '';
    } else if (type === 'FOOTER') {
      newComponent.text = '';
    } else if (type === 'BUTTONS') {
      newComponent.buttons = [{ type: 'QUICK_REPLY', text: '' }];
    }

    setComponents([...components, newComponent]);
    setValue('providerConfig.components', [...components, newComponent]);
  };

  const removeComponent = (index: number) => {
    const removido = components[index];
    const newComponents = components.filter((_, i) => i !== index);
    setComponents(newComponents);
    setValue('providerConfig.components', newComponents);
    // Sem cabeçalho não existe anexo de cabeçalho — deixar para trás vira lixo
    // que a Meta recusa na submissão.
    if (removido?.type === 'HEADER') {
      setValue('providerConfig.headerMedia', undefined, { shouldDirty: true });
    }
  };

  const updateComponent = (index: number, updates: Partial<WhatsAppTemplateComponent>) => {
    const newComponents = [...components];
    const component = { ...newComponents[index], ...updates };

    // ✅ Auto-gerar examples quando há variáveis no BODY component
    if (component.type === 'BODY' && updates.text !== undefined) {
      const text = updates.text || '';
      const variables = text.match(/\{\{\d+\}\}/g) || [];
      const variableCount = variables.length;

      if (variableCount > 0) {
        // Preservar exemplos existentes ou criar novos vazios
        const existingExamples = component.example?.body_text?.[0] || [];
        const newExamples: string[] = [];

        for (let i = 0; i < variableCount; i++) {
          newExamples.push(existingExamples[i] || `exemplo${i + 1}`);
        }

        component.example = {
          body_text: [newExamples]
        };
      } else {
        // Remover examples se não há variáveis
        component.example = undefined;
      }
    }

    newComponents[index] = component;
    setComponents(newComponents);
    setValue('providerConfig.components', newComponents);
  };

  // ══ Anexo do cabeçalho ══════════════════════════════════════════════════
  // Mora em `providerConfig.headerMedia` (irmão de `components`), porque é a
  // URL usada no ENVIO. O identificador que a Meta exige para APROVAR
  // (`example.header_handle`) é gerado pelo backend na submissão, a partir
  // desta URL — a tela não fala com a Meta.
  const headerMedia = watch('providerConfig.headerMedia') as TemplateHeaderMedia | undefined;
  const urlDoAnexo = (headerMedia?.url ?? '').trim();

  const gravarAnexo = (media: TemplateHeaderMedia | undefined): void => {
    setValue('providerConfig.headerMedia', media, { shouldDirty: true });
  };

  /**
   * Troca o formato do cabeçalho.
   *
   * O formato manda no resto: TEXTO usa texto, IMAGEM/VÍDEO/DOCUMENTO usam
   * arquivo. Ao trocar, o que sobrou do formato anterior é limpo — texto preso
   * num cabeçalho de mídia vira placeholder inválido na Meta, e anexo preso num
   * cabeçalho de texto é descartado em silêncio no envio.
   */
  const trocarFormatoDoCabecalho = (index: number, formato: FormatoDeCabecalho): void => {
    const cabecalhoAtual = components[index];
    const mudouDeFormato = cabecalhoAtual?.format !== formato;
    const comArquivo = ehFormatoComArquivo(formato);

    // O identificador do arquivo de exemplo (`example.header_handle`) vale só
    // para o tipo de mídia com que foi gerado. Se ele sobrevive à troca de
    // formato, tudo passa a mentir: a dispensa do arquivo (aqui e no backend)
    // olha só se o identificador EXISTE, então a tela deixa salvar sem arquivo,
    // o backend pula o upload na submissão e manda à Meta um cabeçalho de
    // imagem com identificador de documento. Trocar o formato aposenta o
    // identificador; o resto do `example` fica.
    const exemploAtual = cabecalhoAtual?.example;
    const aposentarIdentificador = mudouDeFormato && exemploAtual?.header_handle !== undefined;

    updateComponent(index, {
      format: formato,
      ...(comArquivo && { text: '' }),
      // `example: undefined` é intencional quando não sobra nada no exemplo.
      ...(aposentarIdentificador && { example: semIdentificadorDoArquivo(exemploAtual) }),
    });

    if (!comArquivo) {
      gravarAnexo(undefined);
      return;
    }
    // Trocar entre IMAGEM/VÍDEO/DOCUMENTO mantém o arquivo, mas o tipo tem que
    // seguir o formato: a Meta valida o parâmetro do envio contra o cabeçalho
    // aprovado e devolve (#100) quando os dois discordam. O nome do arquivo é
    // exclusivo de DOCUMENTO — sair dele descarta o nome em vez de guardar
    // um dado que ninguém mais lê.
    if (headerMedia) {
      const { filename } = headerMedia;
      gravarAnexo({
        type: TIPO_DA_MIDIA[formato],
        url: headerMedia.url,
        ...(formato === 'DOCUMENT' && filename !== undefined && filename !== '' && { filename }),
      });
    }
  };

  const definirUrlDoAnexo = (url: string, formato: FormatoComArquivo): void => {
    const filename = headerMedia?.filename;
    gravarAnexo({
      type: TIPO_DA_MIDIA[formato],
      url,
      ...(filename !== undefined && filename !== '' && { filename }),
    });
  };

  const definirNomeDoArquivo = (nome: string, formato: FormatoComArquivo): void => {
    gravarAnexo({
      type: TIPO_DA_MIDIA[formato],
      url: headerMedia?.url ?? '',
      ...(nome !== '' && { filename: nome }),
    });
  };

  const addButton = (componentIndex: number) => {
    const component = components[componentIndex];
    if (component.type === 'BUTTONS') {
      const newButtons = [...(component.buttons || []), { type: 'QUICK_REPLY' as const, text: '' }];
      updateComponent(componentIndex, { buttons: newButtons });
    }
  };

  const removeButton = (componentIndex: number, buttonIndex: number) => {
    const component = components[componentIndex];
    if (component.type === 'BUTTONS') {
      const newButtons = component.buttons?.filter((_, i) => i !== buttonIndex);
      updateComponent(componentIndex, { buttons: newButtons });
    }
  };

  const updateButton = (componentIndex: number, buttonIndex: number, updates: any) => {
    const component = components[componentIndex];
    if (component.type === 'BUTTONS' && component.buttons) {
      const newButtons = [...component.buttons];
      newButtons[buttonIndex] = { ...newButtons[buttonIndex], ...updates };
      updateComponent(componentIndex, { buttons: newButtons });
    }
  };

  const hasHeader = components.some((c) => c.type === 'HEADER');
  const hasFooter = components.some((c) => c.type === 'FOOTER');
  const hasButtons = components.some((c) => c.type === 'BUTTONS');

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
          Categoria do Template <span style={{ color: 'var(--theme-error-color)' }}>*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CATEGORIES.map((cat) => (
            <label
              key={cat.value}
              className="relative flex cursor-pointer rounded-lg border-2 p-4 transition-colors"
              style={{
                borderColor: category === cat.value ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                backgroundColor: category === cat.value ? 'var(--theme-primary-background)' : 'var(--theme-input-background)',
              }}
            >
              <input
                type="radio"
                {...register('providerConfig.category')}
                value={cat.value}
                className="sr-only"
              />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  {cat.label}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                  {cat.description}
                </p>
              </div>
            </label>
          ))}
        </div>
        {(errors.providerConfig as any)?.category && (
          <p className="text-sm mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {((errors.providerConfig as any).category as any).message as string}
          </p>
        )}
      </div>

      {/* Language Selection */}
      <Select
        label="Idioma"
        required
        placeholder="Selecione um idioma"
        options={LANGUAGES.map((lang) => ({
          value: lang.value,
          label: lang.label,
        }))}
        value={watch('providerConfig.language') || ''}
        onChange={(val) => setValue('providerConfig.language', val)}
        error={(errors.providerConfig as any)?.language?.message as string}
      />

      {/* Components Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-card-text)' }}>
            Componentes do Template
          </h3>
          <div className="flex gap-2">
            {!hasHeader && (
              <button
                type="button"
                onClick={() => addComponent('HEADER')}
                data-testid="template-add-header"
                className="px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-card-text)',
                }}
              >
                <Icon name="plus" className="w-4 h-4" />
                Header
              </button>
            )}
            {!hasFooter && (
              <button
                type="button"
                onClick={() => addComponent('FOOTER')}
                data-testid="template-add-footer"
                className="px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-card-text)',
                }}
              >
                <Icon name="plus" className="w-4 h-4" />
                Footer
              </button>
            )}
            {!hasButtons && (
              <button
                type="button"
                onClick={() => addComponent('BUTTONS')}
                data-testid="template-add-buttons"
                className="px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-card-text)',
                }}
              >
                <Icon name="plus" className="w-4 h-4" />
                Botões
              </button>
            )}
          </div>
        </div>

        {/* Components List */}
        <div className="space-y-4">
          {components.map((component, index) => (
            <div
              key={index}
              className="rounded-lg border p-4"
              style={{
                backgroundColor: 'var(--theme-input-background)',
                borderColor: 'var(--theme-input-border)',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  {component.type === 'HEADER' && 'Cabeçalho'}
                  {component.type === 'BODY' && 'Corpo (Obrigatório)'}
                  {component.type === 'FOOTER' && 'Rodapé'}
                  {component.type === 'BUTTONS' && 'Botões'}
                </h4>
                {component.type !== 'BODY' && (
                  <button
                    type="button"
                    onClick={() => removeComponent(index)}
                    data-testid={`template-remove-component-${component.type}`}
                    className="p-1 rounded transition-colors"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Header Component */}
              {component.type === 'HEADER' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                      Formato
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {HEADER_FORMATS.map(({ value, label, icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => trocarFormatoDoCabecalho(index, value)}
                          data-testid={`template-header-format-${value}`}
                          className="px-3 py-2 text-xs rounded-lg border transition-colors flex flex-col items-center gap-1"
                          style={{
                            backgroundColor: component.format === value ? 'var(--theme-primary-background)' : 'transparent',
                            borderColor: component.format === value ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                            color: component.format === value ? 'var(--theme-primary)' : 'var(--theme-card-text)',
                          }}
                        >
                          <Icon name={icon} className="w-4 h-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                    <p
                      className="text-xs mt-1.5"
                      style={{ color: 'var(--theme-muted-text)' }}
                      data-testid="template-header-format-hint"
                    >
                      O formato manda no resto: Texto pede um texto; Imagem, Vídeo e Documento pedem
                      um arquivo. Trocar o formato limpa o que foi preenchido no anterior.
                    </p>
                  </div>

                  {component.format === 'TEXT' && (
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                        Texto do Cabeçalho
                      </label>
                      <input
                        type="text"
                        value={component.text || ''}
                        onChange={(e) => updateComponent(index, { text: e.target.value })}
                        placeholder="Digite o texto do cabeçalho"
                        data-testid="template-header-text"
                        className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                        style={{
                          backgroundColor: 'var(--theme-input-background)',
                          borderColor: 'var(--theme-input-border)',
                          color: 'var(--theme-input-text)',
                        }}
                      />
                    </div>
                  )}

                  {/* Arquivo do cabeçalho — só para IMAGEM/VÍDEO/DOCUMENTO.
                      Campos empilhados, um abaixo do outro (molde do GatewayForm). */}
                  {ehFormatoComArquivo(component.format) && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                          Arquivo do Cabeçalho <span style={{ color: 'var(--theme-error-color)' }}>*</span>
                        </label>
                        <UploadInput
                          value={headerMedia?.url ?? ''}
                          onChange={(url) => definirUrlDoAnexo(url, component.format as FormatoComArquivo)}
                          accept={ARQUIVOS_ACEITOS[component.format]}
                          folder="templates"
                          placeholder="https://exemplo.com/arquivo.pdf"
                          testId="template-header-anexo"
                        />
                        <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                          Este é o arquivo de exemplo que a Meta usa para aprovar o template. No
                          envio o arquivo pode ser outro — a nota fiscal daquele disparo, por exemplo.
                        </p>
                        {urlDoAnexo === '' && !jaTemIdentificadorDaMeta(component) && (
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--theme-error-color)' }}
                            data-testid="template-header-anexo-error"
                            role="alert"
                          >
                            Escolha o arquivo do cabeçalho. Sem ele a Meta recusa o template.
                          </p>
                        )}
                        {urlDoAnexo === '' && jaTemIdentificadorDaMeta(component) && (
                          <p
                            className="text-xs mt-1"
                            style={{ color: 'var(--theme-muted-text)' }}
                            data-testid="template-header-anexo-ja-aprovado"
                          >
                            O arquivo de exemplo já foi enviado à Meta neste template. Preencha
                            acima só se quiser trocar o arquivo.
                          </p>
                        )}
                      </div>

                      {component.format === 'DOCUMENT' && (
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                            Nome do Arquivo (opcional)
                          </label>
                          <Input
                            type="text"
                            value={headerMedia?.filename ?? ''}
                            onChange={(e) => definirNomeDoArquivo(e.target.value, 'DOCUMENT')}
                            placeholder="documento.pdf"
                            data-testid="template-header-anexo-filename"
                          />
                          <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                            Nome que o cliente vê ao receber o arquivo.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Body Component */}
              {component.type === 'BODY' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                      Mensagem Principal
                    </label>
                    <textarea
                      value={component.text || ''}
                      onChange={(e) => updateComponent(index, { text: e.target.value })}
                      placeholder="Digite a mensagem. Use {{1}}, {{2}}, etc. para variáveis"
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <Icon name="alert-circle" className="w-4 h-4" style={{ color: 'var(--theme-info-color)' }} />
                      <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                        Use variáveis numéricas: {'{'}
                        {'{'}1{'}}'}
                        {'}'}, {'{'}
                        {'{'}2{'}}'}
                        {'}'}, {'{'}
                        {'{'}3{'}}'}
                        {'}'}
                      </p>
                    </div>
                  </div>

                  {/* Exemplos de Variáveis */}
                  {component.example?.body_text?.[0] && component.example.body_text[0].length > 0 && (
                    <div
                      className="p-3 rounded-lg border"
                      style={{
                        backgroundColor: 'var(--theme-info-background)',
                        borderColor: 'var(--theme-info-color)',
                      }}
                    >
                      <label className="block text-xs font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
                        📋 Exemplos das Variáveis (Obrigatório para WhatsApp)
                      </label>
                      <div className="space-y-2">
                        {component.example.body_text[0].map((example, exampleIndex) => (
                          <div key={exampleIndex}>
                            <label className="block text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                              Exemplo para {'{'}
                              {'{'}
                              {exampleIndex + 1}
                              {'}}'}
                              {'}'}
                            </label>
                            <input
                              type="text"
                              value={example}
                              onChange={(e) => {
                                const newExamples = [...(component.example?.body_text?.[0] || [])];
                                newExamples[exampleIndex] = e.target.value;
                                updateComponent(index, {
                                  example: {
                                    body_text: [newExamples]
                                  }
                                });
                              }}
                              placeholder={`Valor de exemplo para {{${exampleIndex + 1}}}`}
                              className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                              style={{
                                backgroundColor: 'var(--theme-input-background)',
                                borderColor: 'var(--theme-input-border)',
                                color: 'var(--theme-input-text)',
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                        💡 O WhatsApp usa esses exemplos para aprovar o template. Use valores realistas.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer Component */}
              {component.type === 'FOOTER' && (
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                    Texto do Rodapé
                  </label>
                  <input
                    type="text"
                    value={component.text || ''}
                    onChange={(e) => updateComponent(index, { text: e.target.value })}
                    placeholder="Digite o texto do rodapé"
                    className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-input-text)',
                    }}
                  />
                </div>
              )}

              {/* Buttons Component */}
              {component.type === 'BUTTONS' && (
                <div className="space-y-3">
                  {component.buttons?.map((button, btnIndex) => (
                    <div key={btnIndex} className="flex gap-2">
                      <Select
                        options={BUTTON_TYPES.map((type) => ({
                          value: type.value,
                          label: type.label,
                        }))}
                        value={button.type}
                        onChange={(val) => updateButton(index, btnIndex, { type: val, url: undefined, phone_number: undefined })}
                        fullWidth={false}
                        style={{ minWidth: '150px' }}
                      />
                      <input
                        type="text"
                        value={button.text}
                        onChange={(e) => updateButton(index, btnIndex, { text: e.target.value })}
                        placeholder="Texto do botão"
                        className="flex-1 px-3 py-2 rounded-lg border text-sm h-[38px]"
                        style={{
                          backgroundColor: 'var(--theme-input-background)',
                          borderColor: 'var(--theme-input-border)',
                          color: 'var(--theme-input-text)',
                        }}
                      />
                      {button.type === 'URL' && (
                        <input
                          type="text"
                          value={button.url || ''}
                          onChange={(e) => updateButton(index, btnIndex, { url: e.target.value })}
                          placeholder="https://..."
                          className="flex-1 px-3 py-2 rounded-lg border text-sm h-[38px]"
                          style={{
                            backgroundColor: 'var(--theme-input-background)',
                            borderColor: 'var(--theme-input-border)',
                            color: 'var(--theme-input-text)',
                          }}
                        />
                      )}
                      {button.type === 'PHONE_NUMBER' && (
                        <input
                          type="text"
                          value={button.phone_number || ''}
                          onChange={(e) => updateButton(index, btnIndex, { phone_number: e.target.value })}
                          placeholder="+55..."
                          className="flex-1 px-3 py-2 rounded-lg border text-sm h-[38px]"
                          style={{
                            backgroundColor: 'var(--theme-input-background)',
                            borderColor: 'var(--theme-input-border)',
                            color: 'var(--theme-input-text)',
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeButton(index, btnIndex)}
                        className="p-2 rounded transition-colors"
                        style={{ color: 'var(--theme-error-color)' }}
                      >
                        <Icon name="trash" className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(component.buttons?.length || 0) < 3 && (
                    <button
                      type="button"
                      onClick={() => addButton(index)}
                      className="px-3 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-card-text)',
                      }}
                    >
                      <Icon name="plus" className="w-4 h-4" />
                      Adicionar Botão
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
